import { Controller } from "@hotwired/stimulus";
import { createConsumer } from "@rails/actioncable";

export default class extends Controller {
  static targets = ["container", "terminalList", "status"];

  connect() {
    this.workstationToken = localStorage.getItem('workstation_token');
    console.log('Badge login controller connected, token:', this.workstationToken ? 'present' : 'absent');
    
    if (this.workstationToken) {
      this._fetchTerminals();
      this._hideFallback();
    } else {
      this._showFallback();
    }
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  async _fetchTerminals() {
    try {
      const response = await fetch('/login_terminals', {
        headers: { 'Authorization': `Bearer ${this.workstationToken}` }
      });

      if (!response.ok) {
        // Si le token n'est plus valide, on supprime le localStorage et on affiche le fallback
        if (response.status === 401) {
          localStorage.removeItem('workstation_token');
          this._showFallback();
          return;
        }
        throw new Error('Erreur de communication avec le serveur.');
      }

      const terminals = await response.json();
      this.terminalListTarget.innerHTML = this._buildTerminalListHTML(terminals);
      this.containerTarget.classList.remove('hidden');

    } catch (error) {
      // En cas d'erreur réseau, on affiche un message d'erreur mais on garde le token
      this.containerTarget.classList.remove('hidden');
      this.statusTarget.innerHTML = `
        <div class="text-center">
          <div class="mx-auto flex items-center justify-center h-8 w-8 rounded-full bg-red-100 mb-2">
            <svg class="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p class="text-sm font-medium text-red-900">Erreur de connexion</p>
          <p class="text-xs text-red-600 mt-1">Impossible de contacter le serveur</p>
        </div>
      `;
    }
  }
  
  _buildTerminalListHTML(terminals) {
    if (terminals.length === 0) {
      return '<p class="text-gray-500 text-center">Aucun terminal disponible</p>';
    }

    return `
      <div class="space-y-2">
        <h3 class="text-sm font-medium text-gray-700 mb-3">Sélectionnez un terminal :</h3>
        <div class="grid gap-2">
          ${terminals.map(terminal => 
            `<button data-action="click->badge-login#selectTerminal" 
                     data-identifier="${terminal.identifier}"
                     class="w-full text-left px-4 py-3 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sz-primary-500 focus:border-sz-primary-500">
               <div class="flex items-center">
                 <div class="flex-1">
                   <div class="text-sm font-medium text-gray-900">${terminal.name}</div>
                   <div class="text-xs text-gray-500">${terminal.identifier}</div>
                 </div>
                 <div class="flex items-center space-x-2">
                   <div class="${this._getTerminalBadgeClass(terminal.physical_status)}" 
                        data-terminal-status-subscription-target="badgeStatus"
                        data-terminal-identifier="${terminal.identifier}"
                        title="${this._getTerminalBadgeTitle(terminal.physical_status)}"></div>
                   <svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                   </svg>
                 </div>
               </div>
             </button>`
          ).join('')}
        </div>
      </div>
    `;
  }

  selectTerminal(event) {
    const terminalIdentifier = event.currentTarget.dataset.identifier;
    const terminalName = event.currentTarget.querySelector('.text-sm.font-medium').textContent;
    
    // Désactiver tous les boutons
    this.terminalListTarget.querySelectorAll('button').forEach(btn => {
      btn.disabled = true;
      btn.classList.add('opacity-50', 'cursor-not-allowed');
    });

    this.statusTarget.innerHTML = `
      <div class="flex items-center justify-center space-x-2">
        <svg class="animate-spin h-4 w-4 text-sz-primary-600" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"></circle>
          <path fill="currentColor" class="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>Connexion au terminal ${terminalName}...</span>
      </div>
    `;
    
    this._subscribeToChannel(terminalIdentifier, terminalName);
  }

  _subscribeToChannel(terminalIdentifier, terminalName) {
    if (this.subscription) { 
      this.subscription.unsubscribe(); 
    }

    this.consumer = createConsumer();
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: "BadgeLoginChannel",
        workstation_token: this.workstationToken,
        terminal_identifier: terminalIdentifier
      },
      {
        connected: () => {
          this.statusTarget.innerHTML = `
            <div class="text-center">
              <div class="mx-auto flex items-center justify-center h-8 w-8 rounded-full bg-green-100 mb-2">
                <svg class="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p class="text-sm font-medium text-gray-900">Prêt pour la connexion</p>
              <p class="text-xs text-gray-500 mt-1">Scannez votre badge sur le terminal ${terminalName}</p>
            </div>
          `;
        },
        rejected: () => {
          this.statusTarget.innerHTML = `
            <div class="text-center">
              <div class="mx-auto flex items-center justify-center h-8 w-8 rounded-full bg-red-100 mb-2">
                <svg class="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p class="text-sm font-medium text-red-900">Erreur de connexion</p>
              <p class="text-xs text-red-600 mt-1">Le terminal ${terminalName} est déjà utilisé par un autre poste.</p>
            </div>
          `;
          // Réactiver les boutons
          this._enableTerminalButtons();
        },
        received: (data) => {
          if (data.login_token) {
            this.statusTarget.innerHTML = `
              <div class="text-center">
                <div class="mx-auto flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 mb-2">
                  <svg class="animate-spin h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"></circle>
                    <path fill="currentColor" class="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <p class="text-sm font-medium text-gray-900">Badge détecté</p>
                <p class="text-xs text-gray-500 mt-1">Connexion en cours...</p>
              </div>
            `;
            window.location.href = `/sessions/create_with_token?token=${data.login_token}`;
          } else if (data.error) {
            this.statusTarget.innerHTML = `
              <div class="text-center">
                <div class="mx-auto flex items-center justify-center h-8 w-8 rounded-full bg-red-100 mb-2">
                  <svg class="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p class="text-sm font-medium text-red-900">Erreur</p>
                <p class="text-xs text-red-600 mt-1">${data.error}</p>
              </div>
            `;
            // Réactiver les boutons après une erreur
            setTimeout(() => this._enableTerminalButtons(), 3000);
          }
        }
      }
    );
  }

  _enableTerminalButtons() {
    this.terminalListTarget.querySelectorAll('button').forEach(btn => {
      btn.disabled = false;
      btn.classList.remove('opacity-50', 'cursor-not-allowed');
    });
  }

  _showFallback() {
    const fallback = document.getElementById('badge-fallback');
    if (fallback) {
      fallback.classList.remove('hidden');
    }
    this.containerTarget.classList.add('hidden');
  }

  _hideFallback() {
    const fallback = document.getElementById('badge-fallback');
    if (fallback) {
      fallback.classList.add('hidden');
    }
  }

  _getTerminalBadgeClass(physicalStatus) {
    const baseClasses = "inline-block w-3 h-3 rounded-full";
    
    switch (physicalStatus) {
      case "ready":
        return `${baseClasses} bg-green-500`;
      case "error":
        return `${baseClasses} bg-yellow-500`;
      case "offline":
        return `${baseClasses} bg-red-500`;
      default:
        // "unknown" ou statut indéfini - gris avec animation pulse
        return `${baseClasses} bg-gray-300 animate-pulse`;
    }
  }

  _getTerminalBadgeTitle(physicalStatus) {
    switch (physicalStatus) {
      case "ready":
        return "Terminal connecté et opérationnel";
      case "error":
        return "Terminal connecté mais avec des erreurs";
      case "offline":
        return "Terminal hors ligne";
      default:
        return "Connexion en cours...";
    }
  }
}
